import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getPlantHealthColor, getPlantHealthIcon } from './mockData';
import PlantMetadataForm from './PlantMetadataForm';
import { savePlantsToStorage, loadPlantsFromStorage } from '../../utils/plantStorageUtils';

export default function SnapshotViewer({ 
    currentSnapshot, 
    plants: externalPlants, 
    selectedPlant, 
    onPlantSelect,
    onSnapshotClick,
    onPlantsUpdate,
    imageRef: externalImageRef
}) {
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [newBox, setNewBox] = useState(null);
    const [draggedPlant, setDraggedPlant] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [resizingPlant, setResizingPlant] = useState(null);
    const [resizeHandle, setResizeHandle] = useState(null);
    const [resizeStart, setResizeStart] = useState({ mouseX: 0, mouseY: 0, x: 0, y: 0, width: 0, height: 0 });
    const [showMetadataForm, setShowMetadataForm] = useState(false);
    const [pendingBox, setPendingBox] = useState(null);
    const [localPlants, setLocalPlants] = useState(externalPlants);
    const localImageRef = useRef(null);
    const imageRef = externalImageRef || localImageRef;
    const containerRef = useRef(null);
    const currentSnapshotRef = useRef(null);

    // Get snapshot ID for storage
    const getSnapshotId = useCallback(() => {
        return currentSnapshot?.id || 'default';
    }, [currentSnapshot]);

    // Handle snapshot changes - load plants from localStorage
    useEffect(() => {
        if (currentSnapshot && currentSnapshotRef.current !== currentSnapshot.id) {
            currentSnapshotRef.current = currentSnapshot.id;
            
            const storedPlants = loadPlantsFromStorage(getSnapshotId());
            
            // Immediately set local plants with no delay
            setLocalPlants(storedPlants);
            
            // Don't notify parent at all - let parent handle its own state
            // This breaks the circular dependency completely
        }
    }, [currentSnapshot, getSnapshotId]);

    // Only sync FROM external TO local when external actually changes from user actions
    // (not from snapshot loading), and only when not in the middle of operations
    useEffect(() => {
        // Only sync if:
        // 1. Not in middle of any operation
        // 2. External plants actually changed
        // 3. We're not in the process of loading a snapshot
        if (!showMetadataForm && 
            !pendingBox && 
            !draggedPlant && 
            !resizingPlant &&
            currentSnapshotRef.current === currentSnapshot?.id) {
            
            // Only update if genuinely different
            const plantsChanged = JSON.stringify(externalPlants) !== JSON.stringify(localPlants);
            if (plantsChanged) {
                setLocalPlants(externalPlants);
            }
        }
    }, [externalPlants, showMetadataForm, pendingBox, draggedPlant, resizingPlant, currentSnapshot]);

    // Save plants to localStorage whenever local plants change
    useEffect(() => {
        if (localPlants.length > 0 && currentSnapshot) {
            savePlantsToStorage(localPlants, getSnapshotId());
        }
    }, [localPlants, currentSnapshot, getSnapshotId]);



    // Get relative coordinates within the image container
    const getRelativeCoordinates = useCallback((e) => {
        if (!containerRef.current) return { x: 0, y: 0 };
        const rect = containerRef.current.getBoundingClientRect();
        return {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
    }, []);

    // Get image boundaries for constraint checking
    const getImageBoundaries = useCallback(() => {
        if (!imageRef.current || !containerRef.current) {
            return { width: 0, height: 0 };
        }
        
        const imageRect = imageRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        
        return {
            width: imageRect.width,
            height: imageRect.height,
            offsetX: imageRect.left - containerRect.left,
            offsetY: imageRect.top - containerRect.top
        };
    }, []);

    // Constrain bounding box within image boundaries
    const constrainToImageBounds = useCallback((box) => {
        const boundaries = getImageBoundaries();
        if (!boundaries.width || !boundaries.height) return box;

        const minX = boundaries.offsetX;
        const minY = boundaries.offsetY;
        const maxX = boundaries.offsetX + boundaries.width - box.width;
        const maxY = boundaries.offsetY + boundaries.height - box.height;

        return {
            ...box,
            x: Math.max(minX, Math.min(maxX, box.x)),
            y: Math.max(minY, Math.min(maxY, box.y)),
            width: Math.min(box.width, boundaries.width),
            height: Math.min(box.height, boundaries.height)
        };
    }, [getImageBoundaries]);

    // Handle mouse down for creating new bounding box
    const handleMouseDown = useCallback((e) => {
        if (!isEditMode || draggedPlant || resizingPlant) return;
        
        const coords = getRelativeCoordinates(e);
        const boundaries = getImageBoundaries();
        
        // Only start drawing if click is within image boundaries
        if (boundaries.width && boundaries.height) {
            const withinBounds = coords.x >= boundaries.offsetX && 
                               coords.x <= boundaries.offsetX + boundaries.width &&
                               coords.y >= boundaries.offsetY && 
                               coords.y <= boundaries.offsetY + boundaries.height;
            
            if (!withinBounds) return;
        }
        
        setIsDrawing(true);
        setNewBox({
            startX: coords.x,
            startY: coords.y,
            x: coords.x,
            y: coords.y,
            width: 0,
            height: 0
        });
    }, [isEditMode, draggedPlant, resizingPlant, getRelativeCoordinates, getImageBoundaries]);

    // Handle mouse move for drawing bounding box, dragging, or resizing
    const handleMouseMove = useCallback((e) => {
        if (resizingPlant) {
            // Handle resizing bounding box
            requestAnimationFrame(() => {
                const coords = getRelativeCoordinates(e);
                const deltaX = coords.x - resizeStart.mouseX;
                const deltaY = coords.y - resizeStart.mouseY;
                
                setLocalPlants(prevPlants => 
                    prevPlants.map(plant => {
                        if (plant.id !== resizingPlant) return plant;
                        
                        const currentBox = plant.boundingBox;
                        let newBox = { ...currentBox };
                        
                        switch (resizeHandle) {
                            case 'nw':
                                // Top-left: adjust position and size
                                newBox.x = resizeStart.x + deltaX;
                                newBox.y = resizeStart.y + deltaY;
                                newBox.width = resizeStart.width - deltaX;
                                newBox.height = resizeStart.height - deltaY;
                                break;
                            case 'ne':
                                // Top-right: adjust position (y only) and size
                                newBox.x = resizeStart.x;
                                newBox.y = resizeStart.y + deltaY;
                                newBox.width = resizeStart.width + deltaX;
                                newBox.height = resizeStart.height - deltaY;
                                break;
                            case 'sw':
                                // Bottom-left: adjust position (x only) and size
                                newBox.x = resizeStart.x + deltaX;
                                newBox.y = resizeStart.y;
                                newBox.width = resizeStart.width - deltaX;
                                newBox.height = resizeStart.height + deltaY;
                                break;
                            case 'se':
                                // Bottom-right: only adjust size
                                newBox.x = resizeStart.x;
                                newBox.y = resizeStart.y;
                                newBox.width = resizeStart.width + deltaX;
                                newBox.height = resizeStart.height + deltaY;
                                break;
                            default:
                                return plant;
                        }
                        
                        // Ensure minimum size
                        if (newBox.width < 20) newBox.width = 20;
                        if (newBox.height < 20) newBox.height = 20;
                        
                        // Apply boundary constraints
                        const constrainedBox = constrainToImageBounds(newBox);
                        
                        return {
                            ...plant,
                            boundingBox: constrainedBox
                        };
                    })
                );
            });
        } else if (draggedPlant) {
            // Handle dragging existing bounding box - use requestAnimationFrame for smooth updates
            requestAnimationFrame(() => {
                const coords = getRelativeCoordinates(e);
                const newX = coords.x - dragOffset.x;
                const newY = coords.y - dragOffset.y;
                
                // Update the dragged plant's position with boundary constraints
                setLocalPlants(prevPlants => 
                    prevPlants.map(plant => {
                        if (plant.id !== draggedPlant) return plant;
                        
                        const proposedBox = {
                            ...plant.boundingBox,
                            x: newX,
                            y: newY
                        };
                        
                        const constrainedBox = constrainToImageBounds(proposedBox);
                        
                        return {
                            ...plant,
                            boundingBox: constrainedBox
                        };
                    })
                );
            });
        } else if (isDrawing && newBox) {
            // Handle drawing new bounding box
            const coords = getRelativeCoordinates(e);
            const width = coords.x - newBox.startX;
            const height = coords.y - newBox.startY;
            
            const proposedBox = {
                x: width < 0 ? coords.x : newBox.startX,
                y: height < 0 ? coords.y : newBox.startY,
                width: Math.abs(width),
                height: Math.abs(height)
            };
            
            // Constrain the new box to image boundaries
            const constrainedBox = constrainToImageBounds(proposedBox);
            
            setNewBox(prev => ({
                ...prev,
                x: constrainedBox.x,
                y: constrainedBox.y,
                width: constrainedBox.width,
                height: constrainedBox.height
            }));
        }
    }, [isDrawing, newBox, draggedPlant, dragOffset, resizingPlant, resizeHandle, resizeStart, getRelativeCoordinates, constrainToImageBounds]);

    // Handle mouse up for finishing bounding box creation, dragging, or resizing
    const handleMouseUp = useCallback(() => {
        if (resizingPlant) {
            // Finish resizing - sync final size with parent
            if (onPlantsUpdate) {
                onPlantsUpdate(localPlants);
            }
            setResizingPlant(null);
            setResizeHandle(null);
            setResizeStart({ mouseX: 0, mouseY: 0, x: 0, y: 0, width: 0, height: 0 });
        } else if (draggedPlant) {
            // Finish dragging - sync final position with parent
            if (onPlantsUpdate) {
                onPlantsUpdate(localPlants);
            }
            setDraggedPlant(null);
            setDragOffset({ x: 0, y: 0 });
        } else if (isDrawing && newBox) {
            // Finish drawing new box
            setIsDrawing(false);
            
            // Only create box if it has minimum size
            if (newBox.width > 20 && newBox.height > 20) {
                setPendingBox(newBox);
                setShowMetadataForm(true);
            }
            
            setNewBox(null);
        }
    }, [isDrawing, newBox, draggedPlant, resizingPlant, localPlants, onPlantsUpdate]);

    // Handle starting to drag a bounding box
    const handleBoxMouseDown = useCallback((e, plant) => {
        if (!isEditMode || resizingPlant) return;
        
        e.stopPropagation();
        const coords = getRelativeCoordinates(e);
        
        setDraggedPlant(plant.id);
        setDragOffset({
            x: coords.x - plant.boundingBox.x,
            y: coords.y - plant.boundingBox.y
        });
    }, [isEditMode, resizingPlant, getRelativeCoordinates]);

    // Handle starting to resize a bounding box
    const handleResizeMouseDown = useCallback((e, plant, handle) => {
        if (!isEditMode) return;
        
        e.stopPropagation();
        const coords = getRelativeCoordinates(e);
        
        setResizingPlant(plant.id);
        setResizeHandle(handle);
        setResizeStart({
            mouseX: coords.x,
            mouseY: coords.y,
            x: plant.boundingBox.x,
            y: plant.boundingBox.y,
            width: plant.boundingBox.width,
            height: plant.boundingBox.height
        });
    }, [isEditMode, getRelativeCoordinates]);

    // Handle plant deletion
    const handleDeletePlant = useCallback((plantId) => {
        const updatedPlants = localPlants.filter(plant => plant.id !== plantId);
        setLocalPlants(updatedPlants);
        
        // Update parent after deletion to prevent jitter
        setTimeout(() => {
            if (onPlantsUpdate) {
                onPlantsUpdate(updatedPlants);
            }
        }, 0);
    }, [localPlants, onPlantsUpdate]);

    // Handle saving new plant metadata
    const handleSavePlant = useCallback((plantData) => {
        if (pendingBox) {
            const newPlant = {
                id: `plant-${Date.now()}`,
                name: plantData.name,
                species: plantData.species,
                health: plantData.health,
                growthStage: plantData.growthStage,
                boundingBox: {
                    x: pendingBox.x,
                    y: pendingBox.y,
                    width: pendingBox.width,
                    height: pendingBox.height
                },
                lastWatered: new Date().toISOString(),
                notes: plantData.notes,
                careHistory: []
            };
            
            const updatedPlants = [...localPlants, newPlant];
            setLocalPlants(updatedPlants);
            
            // Update parent after form is closed to prevent jitter
            if (onPlantsUpdate) {
                onPlantsUpdate(updatedPlants);
            }
        }
        
        setShowMetadataForm(false);
        setPendingBox(null);
    }, [pendingBox, localPlants, onPlantsUpdate]);

    // Toggle edit mode
    const toggleEditMode = useCallback(() => {
        setIsEditMode(prev => !prev);
        // Reset any pending operations
        setIsDrawing(false);
        setNewBox(null);
        setShowMetadataForm(false);
        setPendingBox(null);
    }, []);

    if (!currentSnapshot) {
        return (
            <div className="camera-snapshot-section">
                <div className="no-snapshot">
                    <i className="ri-camera-off-line"></i>
                    <p>No snapshot available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="camera-snapshot-section">
            {/* Edit Button */}
            <button 
                className={`edit-mode-btn ${isEditMode ? 'active' : ''}`}
                onClick={toggleEditMode}
                title={isEditMode ? 'Exit Edit Mode' : 'Edit Bounding Boxes'}
            >
                <i className={isEditMode ? 'ri-check-line' : 'ri-edit-line'}></i>
                {isEditMode ? 'Done' : 'Edit'}
            </button>

            <div className="snapshot-viewer">
                <div 
                    className={`snapshot-container ${isEditMode ? 'edit-mode' : ''}`}
                    onClick={onSnapshotClick}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    ref={containerRef}
                    style={{ cursor: isEditMode ? 'crosshair' : 'default' }}
                >
                    <img 
                        ref={imageRef}
                        src={currentSnapshot.imageUrl} 
                        alt="Plant snapshot" 
                        className="snapshot-image"
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        onMouseDown={(e) => {
                            if (isEditMode) {
                                e.preventDefault();
                            }
                        }}
                        style={{ 
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none'
                        }}
                    />
                    
                    {/* Bounding Boxes Overlay */}
                    <div className="bounding-boxes-overlay">
                        {localPlants.map((plant) => (
                            <div
                                key={plant.id}
                                className={`bounding-box ${selectedPlant === plant.id ? 'selected' : ''} ${isEditMode ? 'editable' : ''} ${draggedPlant === plant.id ? 'dragging' : ''} ${resizingPlant === plant.id ? 'resizing' : ''}`}
                                style={{
                                    left: `${plant.boundingBox.x}px`,
                                    top: `${plant.boundingBox.y}px`,
                                    width: `${plant.boundingBox.width}px`,
                                    height: `${plant.boundingBox.height}px`,
                                    borderColor: getPlantHealthColor(plant.health)
                                }}
                                onMouseDown={(e) => {
                                    if (isEditMode) {
                                        handleBoxMouseDown(e, plant);
                                    }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isEditMode) {
                                    onPlantSelect(plant.id);
                                    }
                                }}
                                title={`${plant.name} - ${plant.health} health`}
                            >
                                <div 
                                    className="bounding-box-label"
                                    style={{ backgroundColor: getPlantHealthColor(plant.health) }}
                                >
                                    {plant.name}
                                </div>
                                <div className={`bounding-box-health ${plant.health}`}>
                                    <i className={getPlantHealthIcon(plant.health)}></i>
                                </div>
                                
                                {/* Edit Mode Controls */}
                                {isEditMode && (
                                    <>
                                        {/* Delete Button */}
                                        <button
                                            className="bounding-box-delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeletePlant(plant.id);
                                            }}
                                            title="Delete Plant"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                        
                                        {/* Resize Handles */}
                                        <div className="resize-handles">
                                            <div 
                                                className="resize-handle nw"
                                                onMouseDown={(e) => handleResizeMouseDown(e, plant, 'nw')}
                                            ></div>
                                            <div 
                                                className="resize-handle ne"
                                                onMouseDown={(e) => handleResizeMouseDown(e, plant, 'ne')}
                                            ></div>
                                            <div 
                                                className="resize-handle sw"
                                                onMouseDown={(e) => handleResizeMouseDown(e, plant, 'sw')}
                                            ></div>
                                            <div 
                                                className="resize-handle se"
                                                onMouseDown={(e) => handleResizeMouseDown(e, plant, 'se')}
                                            ></div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                        
                        {/* New Bounding Box Being Drawn */}
                        {newBox && (
                            <div
                                className="bounding-box drawing"
                                style={{
                                    left: `${newBox.x}px`,
                                    top: `${newBox.y}px`,
                                    width: `${newBox.width}px`,
                                    height: `${newBox.height}px`,
                                    borderColor: '#4CAF50',
                                    borderStyle: 'dashed'
                                }}
                            >
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Plant Metadata Form Modal */}
            {showMetadataForm && (
                <PlantMetadataForm
                    onSave={handleSavePlant}
                    onCancel={() => {
                        setShowMetadataForm(false);
                        setPendingBox(null);
                    }}
                />
            )}
        </div>
    );
}

SnapshotViewer.propTypes = {
    currentSnapshot: PropTypes.shape({
        id: PropTypes.string,
        imageUrl: PropTypes.string,
        timestamp: PropTypes.string
    }),
    plants: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        health: PropTypes.string.isRequired,
        boundingBox: PropTypes.shape({
            x: PropTypes.number.isRequired,
            y: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired,
            height: PropTypes.number.isRequired
        }).isRequired
    })).isRequired,
    selectedPlant: PropTypes.string,
    onPlantSelect: PropTypes.func.isRequired,
    onSnapshotClick: PropTypes.func,
    onPlantsUpdate: PropTypes.func,
    imageRef: PropTypes.shape({
        current: PropTypes.instanceOf(Element)
    })
}; 